import isNonNull from '@openphone/lib/isNonNull'
import { action, makeAutoObservable, onBecomeObserved, reaction, runInAction } from 'mobx'

import StatefulPromise from '@src/lib/StatefulPromise'
import { logError } from '@src/lib/log'
import type Service from '@src/service'
import type { EncodableSubscription } from '@src/service/model'
import { SubscriptionModel } from '@src/service/model'
import makePersistable from '@src/service/storage/makePersistable'
import type {
  CancelParams,
  Coupon,
  CreditCard,
  StartFeaturesPreviewParams,
  SubscriptionEntitlements,
  UpdateParams,
} from '@src/service/transport/billing'

import AddonStore from './AddonStore/AddonStore'
import InvoicesStore from './InvoicesStore'

export default class BillingStore {
  addon: AddonStore
  invoices: InvoicesStore
  /**
   * Prefer using `Billing.getCurrentSubscription()` instead if you expect the current
   * subscription to be ready.
   */
  subscription: SubscriptionModel | null = null
  private subscriptionNotificationRevision = 0
  subscriptionEntitlements: SubscriptionEntitlements | null = null
  fetchSubscriptionEntitlementsPromise = new StatefulPromise(
    this.fetchSubscriptionEntitlements.bind(this),
  )
  coupon: Coupon | null = null
  card: CreditCard | null = null
  /**
   * Because card can be null if the org has no payment method, we need another way to differentiate between not loaded
   * yet vs loaded but empty.
   */
  hasFetchedCard = false
  showUpgradeBanner = true

  /**
   * - Trigger UI changes when a KYC (Know Your Customer) check fails
   * - The client is notified about the KYC failure via WebSocket subscription update
   * - We can determine a failed attempt by comparing the `identityFailedCount` property
   *   between the new and old subscription objects
   */
  hasKycFailed = false

  /**
   * Flag to indicate that subscription creation is in progress.
   * Used to prevent race conditions where addon fetches are triggered
   * before the subscription/start endpoint has fully completed creating
   * addon subscriptions on the backend.
   */
  isStartingSubscription = false

  constructor(private root: Service) {
    this.addon = new AddonStore(root)
    this.invoices = new InvoicesStore(root)
    this.subscribeToWebSocket()
    makeAutoObservable(this, {}, { autoBind: true })
    makePersistable(this, 'BillingStore', {
      subscription: root.storage.async((d: EncodableSubscription) =>
        new SubscriptionModel().deserialize(d),
      ),
      coupon: root.storage.async(),
      showUpgradeBanner: root.storage.sync(),
    })
    onBecomeObserved(this, 'subscriptionEntitlements', () => {
      if (this.fetchSubscriptionEntitlementsPromise.status === 'idle') {
        void this.fetchSubscriptionEntitlementsPromise.run()
      }
    })

    reaction(
      () => this.subscription?.identityFailedCount,
      (count, prevCount) => {
        if (isNonNull(count) && isNonNull(prevCount) && count > prevCount) {
          this.hasKycFailed = true
        }
      },
    )
  }

  /**
   * Returns the current subscription if it's ready, otherwise throws an error.
   *
   * @returns The current subscription
   * @throws If the current subscription is not ready yet
   */
  getCurrentSubscription() {
    const currentSubscription = this.subscription

    if (!currentSubscription) {
      throw new Error(
        'BillingStore.getCurrentSubscription() called before the current subscription was ready',
      )
    }

    return currentSubscription
  }

  get isSubscriptionReviewNull(): boolean {
    return (
      this.subscription?.reviewStatus === null &&
      this.root.phoneNumber.loaded &&
      this.root.user.phoneNumbers.length > 0
    )
  }

  fetchSubscription() {
    const notificationRevision = this.subscriptionNotificationRevision

    return this.root.transport.billing.subscription().then(
      action((json) => {
        if (notificationRevision !== this.subscriptionNotificationRevision) {
          return
        }

        this.subscription ??= new SubscriptionModel()
        this.subscription.deserialize(json)
      }),
    )
  }

  async fetchSubscriptionEntitlements() {
    if (!this.root.flags.flags.webScheduledDowngrades) {
      return
    }

    return this.root.transport.billing
      .subscriptionEntitlements()
      .then(
        action((json) => {
          this.subscriptionEntitlements = json
        }),
      )
      .catch(logError)
  }

  fetchIdentityVerificationSecret() {
    return this.root.transport.billing.identityVerificationSecret()
  }

  fetchCards() {
    return this.root.transport.billing.creditCard().then(
      action((res) => {
        // The card response can return {} when there's no payment method on file, so check that each field is present.
        this.card = res?.last4 && res?.exp_month && res?.exp_year ? res : null
        this.hasFetchedCard = true
      }),
    )
  }

  update(params: UpdateParams) {
    return this.root.transport.billing.update(params).then(
      action((res) => {
        this.upsertSubscription(res)
      }),
    )
  }

  start(params: UpdateParams) {
    this.isStartingSubscription = true
    return this.root.transport.billing
      .start(params)
      .then(
        action((res) => {
          this.upsertSubscription(res)
        }),
      )
      .finally(
        action(() => {
          this.isStartingSubscription = false
        }),
      )
  }

  startFallback(param: UpdateParams) {
    this.isStartingSubscription = true
    return this.root.transport.billing
      .startFallback(param)
      .then(
        action((res) => {
          this.upsertSubscription(res)
        }),
      )
      .finally(
        action(() => {
          this.isStartingSubscription = false
        }),
      )
  }

  getSubscriptionIntent() {
    return this.root.transport.billing.getSubscriptionIntent()
  }

  getPaymentIntent() {
    return this.root.transport.billing.getPaymentIntent()
  }

  convertCredits(amount: number) {
    return this.root.transport.billing.convertCredits(amount).then(
      action((res) => {
        this.upsertSubscription(res.subscription)
      }),
    )
  }

  addCredits(amount: number) {
    return this.root.transport.billing.addCredits(amount).then(
      action((res) => {
        this.upsertSubscription(res)
      }),
    )
  }

  autoCharge(amount: number) {
    return this.root.transport.billing.autoCharge(amount).then(
      action((res) => {
        this.upsertSubscription(res)
      }),
    )
  }

  reactivate() {
    return this.root.transport.billing.reactivate().then(
      action((res) => {
        // If the response contains a clientSecret, it means the payment needs 3DS authentication
        if ('clientSecret' in res) {
          return res.clientSecret
        }
        this.upsertSubscription(res)
      }),
    )
  }

  endTrial() {
    return this.root.transport.billing.endTrial().then(
      action((res) => {
        this.upsertSubscription(res)
      }),
    )
  }

  cancel(params: CancelParams) {
    return this.root.transport.billing.cancel(params).then(
      action((res) => {
        this.upsertSubscription(res)
      }),
    )
  }

  upsertCreditCard(paymentMethodId: string) {
    return this.root.transport.billing.upsertCreditCard(paymentMethodId).then(
      action((res) => {
        this.card = res
      }),
    )
  }

  upsertSubscription(subscription: EncodableSubscription) {
    if (this.subscription) {
      this.subscription.deserialize(subscription)
    } else {
      const newSubscription = new SubscriptionModel()
      newSubscription.deserialize(subscription)
      this.subscription = newSubscription
    }
  }

  async fetchCoupon(code: string) {
    const response = await this.root.transport.billing.coupon(code)

    runInAction(() => {
      this.coupon = response
    })
  }

  /**
   * Bypasses the KYC flow
   *
   * @param orgId
   * @returns updated supscription by setting identityVerificationStatus to `failed` & reviewStatus to `needs review`
   */
  declineKyc() {
    return this.root.transport.billing.declineKyc().then(
      action((res) => {
        this.upsertSubscription(res)
      }),
    )
  }

  startFeaturesPreview(params: StartFeaturesPreviewParams) {
    return this.root.transport.billing.startFeaturesPreview(params).then(
      action((res) => {
        this.root.capabilities.load(res.capabilities)
        this.upsertSubscription(res.subscription)
      }),
    )
  }

  private handlePaymentMethodUpdateEvent(card: CreditCard) {
    this.card = card
  }

  private async handleSubscriptionUpdate({
    subscription,
  }: {
    subscription: EncodableSubscription
  }) {
    this.subscriptionNotificationRevision += 1
    this.upsertSubscription(subscription)
    this.fetchSubscriptionEntitlementsPromise.resetStatus()

    await Promise.all([
      this.invoices.fetchUpcomingInvoice(),
      this.fetchSubscriptionEntitlementsPromise.run(),
    ])
  }

  private subscribeToWebSocket() {
    this.root.transport.onNotificationData.subscribe((data) => {
      switch (data.type) {
        case 'subscription-update':
          this.handleSubscriptionUpdate(data).catch(logError)
          return
        case 'payment-method-update':
          this.handlePaymentMethodUpdateEvent(data.card)
          return
      }
    })
  }
}
